import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import secureRandomPassword from 'secure-random-password';
import AvatarUpload from '../pages/user/profile/AvatarUpload';
import { useFileUpload } from 'hooks/useFileUpload';
import { Alert, Button, Card, Col, Input, Row, Space, Typography } from 'antd';
import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const CHARSETS = {
  lower: 'abcdefghijklmnopqrstuvwxyz',
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  numbers: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

const EditableInput = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  isSaving,
  isEditMode,
  disabled,
  type = 'text',
  appendContent,
  autoComplete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);

  const renderInput = (inputValue, onInputChange, inputDisabled) => (
    <Input
      type={type}
      value={inputValue || ''}
      onChange={onInputChange}
      disabled={inputDisabled}
      autoComplete={autoComplete}
      suffix={appendContent || null}
    />
  );

  if (!isEditMode || !onSave) {
    return (
      <div>
        <Text strong>{label}</Text>
        <div style={{ marginTop: 4 }}>
          {renderInput(
            value,
            (e) =>
              onChange({
                target: {
                  name,
                  value: e.target.value,
                },
              }),
            disabled
          )}
        </div>
        {error && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {error}
          </Text>
        )}
      </div>
    );
  }

  const toggleEdit = async () => {
    if (isEditing) {
      if (internalValue === (originalValue || '')) {
        toast.info('No changes detected');
        setIsEditing(false);
        return;
      }

      try {
        await onSave(name, internalValue);
        toast.success(`${label} updated successfully!`);
        setIsEditing(false);
      } catch (err) {
        toast.error(`Failed to update ${label}: ${err.message}`);
      }
      return;
    }

    setIsEditing(true);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <Text strong>{label}</Text>
        <Button type="link" size="small" loading={isSaving} onClick={toggleEdit}>
          {isEditing ? 'Save' : 'Edit'}
        </Button>
      </div>

      {renderInput(
        internalValue,
        (e) => setInternalValue(e.target.value),
        !isEditing || disabled
      )}

      {error && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {error}
        </Text>
      )}
    </div>
  );
};

const AccountForm = ({
  formData = {},
  errors = {},
  handleInputChange,
  userId,
  patchUserProperties,
  patchProfileProperties,
  refetch,
  isEditMode,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [isPhoneEditing, setIsPhoneEditing] = useState(false);
  const originalValuesSetRef = useRef(false);
  const initialPreview =
    formData.profilePhoto && String(formData.profilePhoto).startsWith('http')
      ? formData.profilePhoto
      : null;
  const [previewPhoto, setPreviewPhoto] = useState(initialPreview);

  const {
    uploading,
    error: uploadError,
    uploadFile,
    getFileMetadata,
  } = useFileUpload();

  useEffect(() => {
    return () => {
      if (previewPhoto?.startsWith('blob:')) URL.revokeObjectURL(previewPhoto);
    };
  }, [previewPhoto]);

  useEffect(() => {
    let mounted = true;
    const id = formData.profilePhoto;
    if (!id) return;

    if (String(id).startsWith('http')) {
      setPreviewPhoto(id);
      return;
    }

    (async () => {
      try {
        const meta = await getFileMetadata(id);
        if (!mounted) return;
        const publicUrl = meta?.publicUrl || meta?.url || meta?.fileUrl || null;
        if (publicUrl) setPreviewPhoto(publicUrl);
      } catch {
        // Ignore metadata lookup errors.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [formData.profilePhoto, getFileMetadata]);

  useEffect(() => {
    originalValuesSetRef.current = false;
    setOriginalValues({});
  }, [userId]);

  useEffect(() => {
    if (originalValuesSetRef.current) return;
    if (!formData || Object.keys(formData).length === 0) return;

    const fields = [
      'firstName',
      'middleName',
      'lastName',
      'username',
      'email',
      'phone',
      'mobilePhone',
      'homePhone',
      'workPhone',
    ];

    const originals = {};
    fields.forEach((field) => {
      originals[field] = formData[field] || '';
    });

    setOriginalValues(originals);
    originalValuesSetRef.current = true;
  }, [formData]);

  const handleFieldSave = async (fieldName, value) => {
    if (!userId || !patchUserProperties) return;

    setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

    try {
      await patchUserProperties(userId, [{ name: fieldName, value }]);
      setOriginalValues((prev) => ({ ...prev, [fieldName]: value }));
      handleInputChange({ target: { name: fieldName, value } });
      if (refetch) await refetch();
    } catch (err) {
      toast.error(`Failed to update ${fieldName}: ${err.message}`);
      throw err;
    } finally {
      setSavingFields((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const generatePassword = () => {
    const pwd =
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.lower }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.upper }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.numbers }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.symbols });

    const shuffled = [...pwd].sort(() => Math.random() - 0.5).join('');

    handleInputChange({ target: { name: 'password', value: shuffled } });
    handleInputChange({ target: { name: 'confirmPassword', value: shuffled } });
    setShowPassword(true);
  };

  const handleCopy = () => {
    if (!formData.password) return;
    navigator.clipboard.writeText(formData.password).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const togglePhoneEdit = async (fieldName, label) => {
    if (savingFields[fieldName]) return;

    if (isPhoneEditing) {
      if ((formData[fieldName] || '') === (originalValues[fieldName] || '')) {
        toast.info('No changes detected');
        setIsPhoneEditing(false);
        return;
      }
      try {
        await handleFieldSave(fieldName, formData[fieldName] || '');
        toast.success(`${label} updated successfully!`);
        setIsPhoneEditing(false);
      } catch {
        // Errors are shown in handleFieldSave.
      }
      return;
    }

    setIsPhoneEditing(true);
  };

  const renderPhoneField = (label, fieldName) => (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <Text strong>{label}</Text>
        {isEditMode && (
          <Button
            type="link"
            size="small"
            loading={!!savingFields[fieldName]}
            onClick={() => togglePhoneEdit(fieldName, label)}
          >
            {isPhoneEditing ? 'Save' : 'Edit'}
          </Button>
        )}
      </div>

      <Input
        type="tel"
        value={formData[fieldName] || ''}
        onChange={(e) =>
          handleInputChange({
            target: {
              name: fieldName,
              value: e.target.value,
            },
          })
        }
        disabled={isEditMode ? !isPhoneEditing : false}
        placeholder={label}
        status={errors[fieldName] ? 'error' : undefined}
      />

      {errors[fieldName] && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {errors[fieldName]}
        </Text>
      )}
    </div>
  );

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <Card size="small" style={{ borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <AvatarUpload
            src={previewPhoto}
            disabled={uploading}
            onChange={async (file) => {
              const previewURL = URL.createObjectURL(file);
              setPreviewPhoto(previewURL);

              try {
                const { fileId, file: fileMeta, meta } = await uploadFile(userId, file);

                handleInputChange({ target: { name: 'profilePhoto', value: fileId } });

                const publicUrl =
                  meta?.publicUrl || fileMeta?.publicUrl || fileMeta?.url || fileMeta?.fileUrl || null;
                if (publicUrl) setPreviewPhoto(publicUrl);

                if (isEditMode && patchProfileProperties) {
                  await patchProfileProperties(userId, [
                    { name: 'profilePhoto', value: fileId },
                  ]);
                  toast.success('Profile photo updated successfully!');
                  if (refetch) await refetch();
                } else {
                  toast.info('Profile photo uploaded and queued for account creation.');
                }
              } catch (err) {
                toast.error('Failed to upload profile photo.');
                console.error(err);
              }
            }}
          />
        </div>

        {uploading && (
          <Alert type="info" showIcon message="Uploading profile photo..." style={{ marginBottom: 0 }} />
        )}

        {uploadError && (
          <Alert
            type="error"
            showIcon
            message={`Upload error: ${uploadError}`}
            style={{ marginBottom: 0 }}
          />
        )}
      </Card>

      <Card size="small" style={{ borderRadius: 12 }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            {['firstName', 'middleName', 'lastName'].map((field) => (
              <Col xs={24} md={8} key={field}>
                <EditableInput
                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                  name={field}
                  value={formData[field] || ''}
                  error={errors[field]}
                  onChange={handleInputChange}
                  onSave={isEditMode ? handleFieldSave : null}
                  originalValue={originalValues[field]}
                  isSaving={savingFields[field]}
                  isEditMode={isEditMode}
                />
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <EditableInput
                label="Username"
                name="username"
                value={formData.username || ''}
                error={errors.username}
                onChange={handleInputChange}
                onSave={null}
                isEditMode={false}
                disabled={isEditMode}
              />
            </Col>
            <Col xs={24} md={12}>
              {renderPhoneField('Phone', 'phone')}
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <EditableInput
                label="Email"
                name="email"
                value={formData.email || ''}
                error={errors.email}
                onChange={handleInputChange}
                onSave={null}
                isEditMode={false}
                disabled={isEditMode}
              />
            </Col>
            <Col xs={24} md={12}>
              {renderPhoneField('Mobile Phone', 'mobilePhone')}
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              {renderPhoneField('Home Phone', 'homePhone')}
            </Col>
            <Col xs={24} md={12}>
              {renderPhoneField('Work Phone', 'workPhone')}
            </Col>
          </Row>

          {!isEditMode && (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <EditableInput
                    label="Password*"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password || ''}
                    error={errors.password}
                    onChange={handleInputChange}
                    onSave={null}
                    isEditMode={false}
                    autoComplete="new-password"
                    appendContent={
                      <Button
                        type="text"
                        size="small"
                        icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowPassword((prev) => !prev)}
                      />
                    }
                  />
                </Col>
              </Row>

              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={generatePassword}>
                  Generate Strong Password
                </Button>

                {formData.password && (
                  <Button icon={<CopyOutlined />} onClick={handleCopy}>
                    Copy Password
                  </Button>
                )}

                {copySuccess && <Text type="success">Copied!</Text>}
              </Space>

              {formData.password && (
                <Alert
                  type="info"
                  showIcon
                  message={`Generated Password: ${formData.password}`}
                  style={{ marginBottom: 0 }}
                />
              )}
            </>
          )}
        </Space>
      </Card>
    </Space>
  );
};

AccountForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patchUserProperties: PropTypes.func,
  patchProfileProperties: PropTypes.func,
  refetch: PropTypes.func,
  isEditMode: PropTypes.bool,
};

export default AccountForm;
